"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  if (loading && posts.length === 0) {
    return <div data-section-id="171582" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12 lg:gap-16">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border border-border/50 shadow-md rounded-lg bg-card/50 animate-pulse">
            <AspectRatio ratio={16 / 9} className="bg-muted/20" />
            <CardContent className="p-6 space-y-4">
              <div className="h-6 w-24 bg-muted/20 rounded-lg" />
              <div className="h-8 w-full bg-muted/20 rounded-lg" />
              <div className="h-4 w-full bg-muted/20 rounded-lg" />
              <div className="h-4 w-2/3 bg-muted/20 rounded-lg" />
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-20">
        <p className="text-muted-foreground font-sans text-lg">Unable to retrieve the latest studio updates.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-20">
        <p className="text-muted-foreground font-sans text-lg">Our journal is currently being curated. Check back soon.</p>
      </div>;
  }
  return <div className="space-y-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12 lg:gap-16">
        {posts.map((post, i) => <Card key={post.id} data-index={i} className="overflow-hidden border border-border/60 shadow-lg rounded-lg bg-card/60 backdrop-blur-md transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 flex flex-col h-full group">
            {post.featuredImage ? <Link to={post.link || "#"} className="block overflow-hidden">
                <AspectRatio ratio={16 / 9}>
                  <img src={post.featuredImage} alt={post.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110 grayscale-[20%] group-hover:grayscale-0" loading="lazy" />
                </AspectRatio>
              </Link> : <Link to={post.link || "#"} className="block overflow-hidden">
                <AspectRatio ratio={16 / 9} className="bg-muted/10 flex items-center justify-center">
                  <span className="text-muted-foreground font-sans text-xs tracking-widest uppercase">Visual Pending</span>
                </AspectRatio>
              </Link>}
            
            <CardContent className="p-6 flex flex-col flex-grow">
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                {post.categories && post.categories.length > 0 ? post.categories.slice(0, 2).map((cat, idx) => <Badge key={cat.id} data-index={idx} variant="secondary" className="text-primary bg-primary/5 hover:bg-primary/10 border border-primary/20 font-sans font-semibold uppercase text-[10px] tracking-[0.2em] px-3 py-1 rounded-lg">
                      {cat.name}
                    </Badge>) : <Badge variant="secondary" className="text-primary bg-primary/5 hover:bg-primary/10 border border-primary/20 font-sans font-semibold uppercase text-[10px] tracking-[0.2em] px-3 py-1 rounded-lg">
                    Studio Note
                  </Badge>}
              </div>
              
              <Link to={post.link || "#"} className="block mb-4">
                <CardTitle className="text-2xl md:text-3xl font-serif font-bold text-foreground line-clamp-2 group-hover:text-primary transition-colors duration-300 leading-tight tracking-tight">
                  {post.title}
                </CardTitle>
              </Link>
              
              <div className="text-muted-foreground font-sans text-base leading-loose line-clamp-3 mb-6 flex-grow" dangerouslySetInnerHTML={{
            __html: post.excerpt || ''
          }} />

              <div className="mt-auto pt-5 border-t border-border/20">
                <Link to={post.link || "#"} className="inline-flex items-center font-serif font-bold text-primary hover:text-primary/80 transition-all text-xs uppercase tracking-widest">
                  Explore Details
                  <ArrowRight className="ml-2 w-3 h-3 transition-transform group-hover:translate-x-2" />
                </Link>
              </div>
            </CardContent>
          </Card>)}
      </div>

      {hasNext && <div className="flex justify-center mt-12">
          <Button onClick={async () => await loadMore()} disabled={isRefetching} size="lg" className="rounded-lg px-12 font-serif font-bold tracking-[0.2em] uppercase text-xs shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300">
            {isRefetching ? "Retrieving..." : "View More Insights"}
          </Button>
        </div>}
    </div>;
}
export default function MainBlogPosts() {
  const wp_query_params = {
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="gallery" className="bg-background text-foreground py-20 md:py-32 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <PostsProvider wp_query={{
        per_page: 6,
        order: "desc",
        orderby: "date"
      }}>
          <PostsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}